/**
 * The one-button Moodle connect (#679), shared by `/welcome` and Settings so the two screens cannot drift.
 *
 * The browser extension marks the page and answers a request with a status word; `connectMoodleWithExtension` is the only
 * thing that talks to it, and it believes `connected` only once the server's own `GET /moodle/connection` says so. No
 * token is ever read here: it goes from Moodle to the server inside the extension. The key path is untouched, and
 * "Use a key instead" reaches it in every state.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthSession } from '../../app/account-control.js';
import { useApi } from '../../app/api-context.js';
import { queryKeys } from '../../api/query-keys.js';
import { useIsPhone } from '../../app/use-is-phone.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';
import { CONNECTOR_STORE_URL } from './connector-store.js';
import { connectMoodleWithExtension, isConnectorInstalled, type MoodleExtensionResult } from './moodle-extension.js';
import { FailureNotice } from '../../app/failure-notice.js';
import { useMoodleConnect } from './moodle-section.js';

export const connectorMessages = defineMessages({
  en: {
    failedT: "That didn't connect",
    cancelT: 'Connecting stopped',
    failB: 'Try again, or use a key instead.',
    addBtn: 'Add the DaVinci connector',
    addLine:
      'A small browser add-on. Add it once, then Moodle connects with one button.',
    connectBtn: 'Connect Moodle',
    connectLine:
      'This uses the Moodle account open in this browser. DaVinci gets your Moodle mobile key and only reads with it. It never sees your password.',
    waitT: 'Connecting. If a tab opens, sign in to Moodle there.',
    retryBtn: 'Try again',
    keyLink: 'Use a key instead',
    noneLine: 'One-button connecting works in Chrome or Edge on a computer.',
    forLabel: 'Connecting for',
    mismatchT: 'This Moodle account is different from the one already connected. Disconnect it first.',
    asLine: 'Connected as',
    notYou: 'Not you? Disconnect',
    disconnect: 'Disconnect',
  },
  he: {
    failedT: 'החיבור לא הצליח',
    cancelT: 'החיבור הופסק',
    failB: 'אפשר לנסות שוב, או להשתמש במפתח במקום.',
    addBtn: 'הוספת המחבר של DaVinci',
    addLine: 'תוסף קטן לדפדפן. מוסיפים פעם אחת, ואז מודל מתחבר בכפתור אחד.',
    connectBtn: 'חיבור למודל',
    connectLine:
      'החיבור משתמש בחשבון המודל שפתוח בדפדפן הזה. DaVinci מקבלת את מפתח המובייל של מודל וקוראת בעזרתו בלבד. את הסיסמה היא לא רואה.',
    waitT: 'מתחברים. אם נפתחת כרטיסייה, נכנסים בה למודל.',
    retryBtn: 'לנסות שוב',
    keyLink: 'להשתמש במפתח במקום',
    noneLine: 'חיבור בכפתור אחד עובד ב-Chrome או ב-Edge במחשב.',
    forLabel: 'חיבור עבור',
    mismatchT: 'חשבון המודל הזה שונה מהחשבון שכבר מחובר. קודם צריך לנתק אותו.',
    asLine: 'החיבור הוא לחשבון של',
    notYou: 'לא החשבון שלך? ניתוק',
    disconnect: 'ניתוק',
  },
});

/** Chrome or Edge (Chromium): the browsers the extension is built for. */
export function isSupportedBrowser(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent,
): boolean {
  return (
    /\b(Chrome|Edg)\//.test(ua) &&
    !/\b(OPR|SamsungBrowser)\//.test(ua) &&
    !/Android|Mobile/.test(ua)
  );
}

/** The connected Moodle account's name, from this session's connect result only. Untrusted text: render it with `<bdi dir="auto">`. */
function setConnectedAs(queryClient: ReturnType<typeof useQueryClient>, name: string | undefined): void {
  queryClient.setQueryData(queryKeys.moodleConnectedAs, name ?? null);
}

export function useMoodleConnectedAs(): string | null {
  // Never fetched: the cache entry is only written by `setConnectedAs`.
  const { data } = useQuery<string | null>({
    queryKey: queryKeys.moodleConnectedAs,
    queryFn: () => null,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  return typeof data === 'string' && data !== '' ? data : null;
}

/**
 * `Connected as {name}.` then `Not you? Disconnect`: shown only when this session's connect result carried a name, so never
 * on a return visit. The name is Moodle's text: a `<bdi>`, never HTML. Disconnecting returns the learner to connecting.
 */
export function ConnectedAsRow(props: { readonly variant: 'welcome' | 'settings' }): React.JSX.Element | null {
  const t = useMessages(connectorMessages);
  const name = useMoodleConnectedAs();
  const { disconnect } = useMoodleConnect();
  if (name === null) return null;
  return (
    <div className="connected-as" data-variant={props.variant}>
      <p>
        <span>
          {t.asLine} <bdi dir="auto">{name}</bdi>.
        </span>{' '}
        <button
          type="button"
          className="connected-as-link"
          disabled={disconnect.isPending}
          onClick={() => disconnect.mutate()}
        >
          {t.notYou}
        </button>
      </p>
      {disconnect.isError ? <FailureNotice error={disconnect.error} /> : null}
    </div>
  );
}

/** A pasted key was refused as a different Moodle account. No Try again; Settings passes `onDisconnect` to offer the way out. */
export function MismatchNotice(props: { readonly onDisconnect?: () => void }): React.JSX.Element {
  const t = useMessages(connectorMessages);
  return (
    <div className="connector" data-variant={props.onDisconnect === undefined ? 'welcome' : 'settings'}>
      <div className="connector-row connector-row-alert" role="alert">
        <span className="connector-mark" aria-hidden="true">
          !
        </span>
        <span>
          <strong>{t.mismatchT}</strong>
        </span>
      </div>
      {props.onDisconnect === undefined ? null : (
        <button type="button" className="settings-button button-neutral" onClick={props.onDisconnect}>
          {t.disconnect}
        </button>
      )}
    </div>
  );
}

export type ConnectorMode = 'none' | 'install' | 'ready';
export type ConnectorPhase = 'idle' | 'waiting' | 'failed' | 'cancelled' | 'mismatch';

function modeFor(phone: boolean, installed: boolean): ConnectorMode {
  if (phone) return 'none';
  if (installed) return 'ready';
  return isSupportedBrowser() && CONNECTOR_STORE_URL !== null ? 'install' : 'none';
}

export function useMoodleConnector() {
  const api = useApi();
  const queryClient = useQueryClient();
  const phone = useIsPhone();
  const { refresh } = useMoodleConnect();
  const [installed, setInstalled] = useState(() => isConnectorInstalled());
  const [phase, setPhase] = useState<ConnectorPhase>('idle');
  const mounted = useRef(true);
  const phoneRef = useRef(phone);
  phoneRef.current = phone;

  // The learner comes back from installing it: look again whenever the page is looked at again.
  useEffect(() => {
    mounted.current = true;
    setInstalled(isConnectorInstalled());
    const check = (): void => {
      const now = isConnectorInstalled();
      setInstalled(now);
      // The name shown is the signed-in account's, and another tab may have switched it: look again when looked at again.
      // Only where it is shown: `none` mode renders no name, and a failed refetch must not touch the account initial.
      if (document.visibilityState !== 'hidden' && modeFor(phoneRef.current, now) !== 'none') {
        void queryClient.refetchQueries({ queryKey: queryKeys.authSession });
      }
    };
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => {
      mounted.current = false;
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [queryClient]);

  const mode = modeFor(phone, installed);
  // True for nobody until there is a listing, so the line is shown only when a listing exists or the connector is here.
  const showNoneLine = mode === 'none' && CONNECTOR_STORE_URL !== null;

  const starting = useRef(false);
  const start = useCallback((): void => {
    if (starting.current) return;
    starting.current = true;
    // The extension's content script needs the press that is still live: the helper is called in this same tick, before any
    // await or effect, never from a state-driven re-render.
    setPhase('waiting');
    void connectMoodleWithExtension({
      confirmConnected: async () =>
        (await api.getMoodleConnection()).connected === true,
    }).catch((): MoodleExtensionResult => ({ status: 'failed' })).then(({ status, moodleFullName }) => {
      starting.current = false;
      // Held for this session even if the page has moved on (the courses step shows it).
      if (status === 'connected') setConnectedAs(queryClient, moodleFullName);
      if (!mounted.current) return;
      if (status === 'connected') {
        setPhase('idle');
        refresh();
      } else {
        setPhase(status);
        // A late success from the extension shows on the server's answer, which always wins.
        refresh();
      }
    });
  }, [api, refresh, queryClient]);

  const clear = useCallback((): void => setPhase('idle'), []);

  return { mode, phase, start, clear, showNoneLine };
}

export type MoodleConnector = ReturnType<typeof useMoodleConnector>;

/**
 * The connector's rows and buttons for one screen. `variant` only picks the button classes the screen already uses.
 * Renders nothing in `none` mode: the screen's own key path is then the whole story.
 */
export function ConnectorActions(props: {
  readonly connector: MoodleConnector;
  readonly variant: 'welcome' | 'settings';
  readonly onUseKey: () => void;
  /** A pasted key was refused as a different Moodle account: the failure row without Try again. */
  readonly mismatch?: boolean;
  readonly onDisconnect?: () => void;
}): React.JSX.Element | null {
  const t = useMessages(connectorMessages);
  const { mode, phase, start } = props.connector;
  const session = useAuthSession();
  const name = session.isSuccess
    ? [session.data.displayName, session.data.username].find((n) => typeof n === 'string' && n.trim() !== '')
    : undefined;
  if (mode === 'none') return null;
  // Whose Moodle this connects: the account that is signed in. Omitted, never a placeholder, when the session has no name.
  const forLine =
    name === undefined ? null : (
      <p className="connector-for">
        {t.forLabel} <bdi dir="auto">{name}</bdi>
      </p>
    );
  const button =
    props.variant === 'welcome'
      ? 'auth-submit'
      : 'settings-button connector-primary';
  const quiet =
    props.variant === 'welcome' ? 'connector-key-welcome' : 'connector-key-link';
  const mismatch = props.mismatch === true || phase === 'mismatch';
  const failed = phase === 'failed' || phase === 'cancelled' || mismatch;

  return (
    <div className="connector" data-variant={props.variant}>
      {phase === 'waiting' ? (
        <div className="connector-row" role="status">
          <span className="connector-spinner" aria-hidden="true" />
          <strong>{t.waitT}</strong>
        </div>
      ) : null}
      {failed ? (
        <div className="connector-row connector-row-alert" role="alert">
          <span className="connector-mark" aria-hidden="true">
            !
          </span>
          <span>
            <strong>{mismatch ? t.mismatchT : phase === 'cancelled' ? t.cancelT : t.failedT}</strong>
            {mismatch ? null : <span>{t.failB}</span>}
          </span>
        </div>
      ) : null}
      {phase === 'waiting' ? null : failed ? (
        <>
          {mismatch ? (
            props.onDisconnect === undefined ? null : (
              <button type="button" className="settings-button button-neutral" onClick={props.onDisconnect}>
                {t.disconnect}
              </button>
            )
          ) : (
            <button type="button" className={button} onClick={start}>
              {t.retryBtn}
            </button>
          )}
          {forLine}
        </>
      ) : mode === 'install' && CONNECTOR_STORE_URL !== null ? (
        <>
          <a
            className={button}
            href={CONNECTOR_STORE_URL}
            target="_blank"
            rel="noopener"
          >
            {t.addBtn}
          </a>
          <p className="connector-line">{t.addLine}</p>
        </>
      ) : (
        <>
          <button type="button" className={button} onClick={start}>
            {t.connectBtn}
          </button>
          {forLine}
          <p className="connector-line">{t.connectLine}</p>
        </>
      )}
      <button type="button" className={quiet} onClick={props.onUseKey}>
        {t.keyLink}
      </button>
    </div>
  );
}
